import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { EvaluationType } from '@/features/lms/domain/value-objects/EvaluationType';

/**
 * Evaluation DTO for store
 * Simplified version without complex Date objects for persistence
 */
export interface EvaluationDTO {
  id: string;
  courseId: string;
  title: string;
  type: EvaluationType;
  weightPercentage: number;
  dueDate: string; // ISO string for persistence
  courseName?: string;
  submissionCount?: number;
  totalStudents?: number;
  averageGrade?: number;
}

/**
 * Evaluations Store State Interface
 * Manages global state for course evaluations
 */
interface EvaluationsState {
  // Data
  evaluations: EvaluationDTO[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions - Setters
  setEvaluations: (evaluations: EvaluationDTO[]) => void;
  addEvaluation: (evaluation: EvaluationDTO) => void;
  updateEvaluation: (id: string, evaluation: Partial<EvaluationDTO>) => void;
  deleteEvaluation: (id: string) => void;

  // Actions - State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Helpers
  getEvaluationById: (id: string) => EvaluationDTO | undefined;
  getEvaluationsByCourse: (courseId: string) => EvaluationDTO[];
  getEvaluationsByType: (type: EvaluationType) => EvaluationDTO[];
  getUpcomingEvaluations: (limit?: number) => EvaluationDTO[];
  getPastDueEvaluations: () => EvaluationDTO[];
  clearEvaluations: () => void;
  reset: () => void;
}

/**
 * Initial state for evaluations store
 */
const initialState = {
  evaluations: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Evaluations Store
 *
 * Global state management for evaluations using Zustand.
 * Features:
 * - Persistent storage (localStorage)
 * - CRUD operations
 * - Loading and error states
 * - Helper selectors for filtering
 * - Date-based queries
 *
 * Usage:
 * ```typescript
 * const { evaluations, isLoading, setEvaluations } = useEvaluationsStore();
 * ```
 *
 * Note: This store should be populated by use cases, not repositories directly.
 * Follow hexagonal architecture principles.
 */
export const useEvaluationsStore = create<EvaluationsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Setters
      setEvaluations: (evaluations) =>
        set({
          evaluations,
          lastFetched: Date.now(),
          error: null,
        }),

      addEvaluation: (evaluation) =>
        set((state) => ({
          evaluations: [...state.evaluations, evaluation],
          lastFetched: Date.now(),
        })),

      updateEvaluation: (id, updatedEvaluation) =>
        set((state) => ({
          evaluations: state.evaluations.map((evaluation) =>
            evaluation.id === id
              ? { ...evaluation, ...updatedEvaluation }
              : evaluation
          ),
          lastFetched: Date.now(),
        })),

      deleteEvaluation: (id) =>
        set((state) => ({
          evaluations: state.evaluations.filter(
            (evaluation) => evaluation.id !== id
          ),
          lastFetched: Date.now(),
        })),

      // State Management
      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error, isLoading: false }),

      // Helpers
      getEvaluationById: (id) => {
        const state = get();
        return state.evaluations.find((evaluation) => evaluation.id === id);
      },

      getEvaluationsByCourse: (courseId) => {
        const state = get();
        return state.evaluations.filter(
          (evaluation) => evaluation.courseId === courseId
        );
      },

      getEvaluationsByType: (type) => {
        const state = get();
        return state.evaluations.filter(
          (evaluation) => evaluation.type === type
        );
      },

      getUpcomingEvaluations: (limit) => {
        const state = get();
        const now = new Date();
        const upcoming = state.evaluations
          .filter((evaluation) => new Date(evaluation.dueDate) > now)
          .sort(
            (a, b) =>
              new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
          );

        return limit ? upcoming.slice(0, limit) : upcoming;
      },

      getPastDueEvaluations: () => {
        const state = get();
        const now = new Date();
        return state.evaluations
          .filter((evaluation) => new Date(evaluation.dueDate) <= now)
          .sort(
            (a, b) =>
              new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
          );
      },

      clearEvaluations: () =>
        set({
          evaluations: [],
          lastFetched: null,
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'evaluations-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not loading/error states
      partialize: (state) => ({
        evaluations: state.evaluations,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors for optimized component rendering
 * Use these to select specific parts of the state
 */
export const evaluationsSelectors = {
  // Select all evaluations
  selectEvaluations: (state: EvaluationsState) => state.evaluations,

  // Select loading state
  selectIsLoading: (state: EvaluationsState) => state.isLoading,

  // Select error state
  selectError: (state: EvaluationsState) => state.error,

  // Select evaluation by ID
  selectEvaluationById: (id: string) => (state: EvaluationsState) =>
    state.evaluations.find((evaluation) => evaluation.id === id),

  // Select evaluations by course
  selectEvaluationsByCourse: (courseId: string) => (state: EvaluationsState) =>
    state.evaluations.filter((evaluation) => evaluation.courseId === courseId),

  // Select evaluations by type
  selectEvaluationsByType: (type: EvaluationType) => (state: EvaluationsState) =>
    state.evaluations.filter((evaluation) => evaluation.type === type),

  // Select upcoming evaluations
  selectUpcomingEvaluations: (state: EvaluationsState) => {
    const now = new Date();
    return state.evaluations
      .filter((evaluation) => new Date(evaluation.dueDate) > now)
      .sort(
        (a, b) =>
          new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      );
  },

  // Select past due evaluations
  selectPastDueEvaluations: (state: EvaluationsState) => {
    const now = new Date();
    return state.evaluations.filter(
      (evaluation) => new Date(evaluation.dueDate) <= now
    );
  },

  // Select evaluations due this week
  selectEvaluationsDueThisWeek: (state: EvaluationsState) => {
    const now = new Date();
    const endOfWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return state.evaluations.filter((evaluation) => {
      const dueDate = new Date(evaluation.dueDate);
      return dueDate > now && dueDate <= endOfWeek;
    });
  },

  // Select evaluations count
  selectEvaluationsCount: (state: EvaluationsState) => state.evaluations.length,

  // Check if data is stale (older than 5 minutes)
  selectIsStale: (state: EvaluationsState) => {
    if (!state.lastFetched) return true;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() - state.lastFetched > fiveMinutes;
  },
};
